
import { Formik } from 'formik';
import { useMemo, useState } from 'react';
import { object, ObjectSchema, string } from 'yup';
import { Typography } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { createNotInAreaUser, getProviderIsLicensedInState, State } from '@uplift/api';
import { useIsMobile, useL10n } from '@uplift/hooks';
import { useProviderPublicMatchingProfile, useStates } from '@uplift/queries';
import { Autocomplete, Button, ClickToCopy, Modal, ModalActions, ModalContent, TextInput } from '@uplift/ui-v2';
import { FullScreenSelect } from '../../../components/full-screen-select';
import { StepContainer, StepContent, StepDescription, StepForm, StepHeading, StepNavigator, StepNextButton } from '../../components';
import { MatchingFlowTypeEnum, WhereNextData } from '../types';

export type WhereProps = {
  onNext: (data: WhereNextData) => void;
  onSwitchToMarketplace: (data: WhereNextData) => void;
  onNotAvailable: (data: WhereNextData) => void;
  onBack?: () => void;
  stateId: string | null;
  banner?: React.ReactNode;
  matchingFlow: MatchingFlowTypeEnum;
  providerId: string;
};

type WhereFormType = {
  stateId: string;
  notAvailableStateId: string;
  email: string;
};

const STATE_NONE_ID = 'none';

export const Where: React.VFC<WhereProps> = props => {
  const { data: matchingProfile } = useProviderPublicMatchingProfile(props.providerId, { enabled: !!props.providerId });
  // state id or null
  const [ notLicensedInState, setNotLicensedInState ] = useState<string | null>(null);
  const { availableStates } = useStates();

  const isInviteOrDirectFlow = props.matchingFlow !== MatchingFlowTypeEnum.Marketplace;
  const onSubmit = async (values: WhereFormType) => {
    if (values.stateId === STATE_NONE_ID) {
      await createNotInAreaUser(values.notAvailableStateId, values.email);

      return props.onNotAvailable({ stateId: values.notAvailableStateId });
    }


    if (isInviteOrDirectFlow && props.providerId) {
      const { data: isLicensed } = await getProviderIsLicensedInState(props.providerId, values.stateId);

      if (!isLicensed) {
        setNotLicensedInState(values.stateId);
        return;
      }
    }

    return props.onNext({ stateId: values.stateId });
  };

  const onNotLicensedInStateModalClose = (findNewTherapist?: boolean) => {
    if (findNewTherapist) {
      props.onSwitchToMarketplace({ stateId: notLicensedInState });

      return;
    }

    setNotLicensedInState(null);
  };

  const description = useMemo(() => {
    if (isInviteOrDirectFlow) {
      return `We need to confirm ${matchingProfile?.nameDisplay?.defaultDisplay ?? 'this provider'} is licensed in your state`;
    }

    return 'Only providers who are licensed in your state can work with you.';
  }, [ isInviteOrDirectFlow, matchingProfile?.nameDisplay?.defaultDisplay ]);

  return (
    <Formik
      initialValues={{
        stateId: props.stateId || '',
        notAvailableStateId: '',
        email: ''
      }}
      onSubmit={onSubmit}
      validationSchema={validationSchema}
      validateOnMount
    >
      {formik => {
        const isNoneSelected = formik.values.stateId === STATE_NONE_ID;

        return (
          <StepContainer>
            {notLicensedInState && (
              <NotLicensedModal
                providerId={props.providerId}
                stateName={availableStates.find(s => s.id === notLicensedInState)?.fullName || 'this state'}
                onClose={onNotLicensedInStateModalClose}
                matchingFlow={props.matchingFlow}
              />
            )}

            {props.banner}
            <StepContent>
              <StepHeading>Where will you do your sessions?</StepHeading>
              <StepDescription>{description}</StepDescription>
              <StepForm onSubmit={formik.handleSubmit} maxWidth="384px">
                <StateSelectInput
                  name="stateId"
                  states={availableStates.concat({ fullName: 'None of these', shortName: '', id: STATE_NONE_ID })}
                />
                {isNoneSelected && <NotInState />}
              </StepForm>
            </StepContent>
            <StepNavigator onBack={props.onBack}>
              {isNoneSelected ? <StepNextButton endIcon={null}>Notify Me</StepNextButton> : <StepNextButton disabled={!formik.isValid || formik.isSubmitting} />}
            </StepNavigator>
          </StepContainer>
        );
      }}
    </Formik>
  );
};

export type StateSelectInputProps = {
  states: State[];
  name: string;
  label?: string;
};

export const StateSelectInput: React.FC<StateSelectInputProps> = ({ label = 'Your state', ...props }) => {
  const Input = useIsMobile() ? MobileStateSelectInput : DesktopStateSelectInput;

  return <Input label={label} {...props} />;
};

const DesktopStateSelectInput: React.FC<StateSelectInputProps> = ({ states, label, name }) => {
  return (
    <Autocomplete
      disableClearable
      fullWidth
      name={name}
      label={label}
      options={states}
      transformValue={v => v?.id}
      getOptionLabel={opt => opt?.fullName ?? ''}
    />
  );
};


const MobileStateSelectInput: React.FC<StateSelectInputProps> = ({ states, label, name }) => {
  const { classes } = useStateSelectStyles();

  return (
    <FullScreenSelect
      options={states}
      getLabel={(option) => option?.fullName || ''}
      getValue={(option) => option.id}
      getClassName={(option) => option.id === STATE_NONE_ID ? classes.noneOption : undefined}
      name={name}
      label={label}
    />
  );
};

const useStateSelectStyles = makeStyles({ name: 'StateSelect' })(theme => ({
  noneOption: {
    marginTop: theme.spacing(1),
    borderTop: `1px solid ${theme.palette.divider}`
  },
}));


const NotInState: React.VFC = () => {
  const { states } = useStates();
  const { classes } = useNotInStateStyles();

  const notAvailableStates = states.filter(state => !state.available);

  return (
    <div className={classes.root}>
      <Typography className={classes.title}>We’re not in your state yet</Typography>
      <Typography>Sign up to get notified when we are in your state.</Typography>
      <div className={classes.form}>
        <StateSelectInput
          states={notAvailableStates}
          label="Select a state"
          name="notAvailableStateId"
        />
        <TextInput
          name="email"
          label="Email"
          type="email"
          classes={{ formControl: classes.input }}
          color="secondary"
        />
      </div>
    </div>
  );
};

const useNotInStateStyles = makeStyles({ name: 'NotInState' })(theme => ({
  root: {
    marginTop: theme.spacing(2),
    background: theme.palette.grey[100],
    padding: theme.spacing(2),
  },
  title: {
    marginBottom: theme.spacing(1),
    fontWeight: 500,
  },
  form: {
    marginTop: theme.spacing(2),
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2)
  },
  input: {
    height: '56px'
  }
}));

const validationSchema = (): ObjectSchema<WhereFormType> => object({
  stateId: string().nullable().required('Please select your state'),
  email: string().when('stateId', {
    is: STATE_NONE_ID,
    then: s => s.email('Please enter a valid email address').required('Please enter your email address')
  }),
  notAvailableStateId: string().when('stateId', {
    is: STATE_NONE_ID,
    then: s => s.nullable().required('Please select a state')
  })
});


type NotLicensedModalProps = {
  onClose: (findNewTherapist?: boolean) => void;
  stateName: string;
  providerId: string;
  matchingFlow: MatchingFlowTypeEnum;
};

const NotLicensedModal: React.VFC<NotLicensedModalProps> = props => {
  const { data: matchingProfile } = useProviderPublicMatchingProfile(props.providerId);
  const l10n = useL10n();

  const content = useMemo(() => {
    if (props.matchingFlow === MatchingFlowTypeEnum.Invite) {
      return l10n.onboarding.connect.helpText;
    }

    return l10n.onboarding.connect.cantBook(props.stateName);
  }, [ l10n.onboarding.connect, props.matchingFlow, props.stateName ]);

  const actions = useMemo(() => {
    if (props.matchingFlow === MatchingFlowTypeEnum.Invite) {
      return (
        <ClickToCopy text={l10n.helpEmail}>
          <Button variant="contained" color="secondary">
            {l10n.onboarding.connect.copyEmail}
          </Button>
        </ClickToCopy>
      );
    }

    return (
      <Button variant="contained" color="secondary" onClick={() => props.onClose(true)}>
        {l10n.onboarding.connect.findATherapist}
      </Button>
    );
  }, [ l10n.helpEmail, l10n.onboarding.connect, props ]);

  return (
    <Modal
      open
      onClose={() => props.onClose()}
      maxWidth="md"
      title={l10n.onboarding.connect.notLicensed(matchingProfile?.nameDisplay?.full, props.stateName)}
    >
      <ModalContent>
        {content}
      </ModalContent>
      <ModalActions>
        {actions}
      </ModalActions>
    </Modal>
  );
};
